// src/components/RecipeCard.tsx
import React from 'react';
import { Recipe } from '../../api/Types';
import FavoriteButton from './FavoriteButton';
import useRecipeCardImage from '../../hooks/recipes/useRecipeCardImage';
import { Avatar, Badge, Box, BoxLink, Card, CardImage, Flex, Heading, Text } from '../ui';

interface RecipeCardProps {
  recipe: Recipe;
  isFavorite: boolean;
  onFavoriteClick: () => void;
  favoriteDisabled?: boolean;
  labelColor: string; // New prop for label color
  overlayBadgeText?: string;
}

const RecipeCardComponent: React.FC<RecipeCardProps> = ({
  recipe,
  isFavorite,
  onFavoriteClick,
  favoriteDisabled = false,
  labelColor,
  overlayBadgeText,
}) => {
  const {
    currentImageUrl,
    isImageLoaded,
    handleImageLoad,
  } = useRecipeCardImage({
    recipeId: recipe.id,
    image: recipe.image,
  });

  return (
    <Card fullHeight position="relative">
      <BoxLink
        to={`/edit-recipe/${recipe.id}`}
        display="block"
        fullHeight
        textDecoration="none"
        textReset
        aria-label={`Éditer la recette ${recipe.title}${
          recipe.authors ? ` par ${recipe.authors.first_name} ${recipe.authors.name}` : ''
        }`}
      >
        {/* Removed Placeholder div, as it caused issues on iPhone grid mode */}

        {/* Image with transition */}
        <CardImage
          src={currentImageUrl}
          fullHeight
          alt={recipe.title}
          loading="lazy"
          onLoad={handleImageLoad}
          style={{
            filter: 'grayscale(50%)',
            objectFit: 'cover',
            opacity: isImageLoaded ? 1 : 0,
            transition: 'opacity 0.4s ease-in-out',
            // Simplified position, as placeholder is removed
            position: 'static', // Always static
          }}
        />

        <Box
          className="recipe-title-overlay"
          position="absolute"
          top={50}
          start={50}
          positionTranslate="middle"
          paddingY={2}
          style={{
            width: 'calc(100% - 2rem)',
            maxWidth: '300px',
            borderColor: labelColor,
            boxShadow: `0px 0px 0px 1px ${labelColor} inset, 0px 6px 15px rgba(0, 0, 0, 0.4)`,
            zIndex: 10,
            overflow: 'visible',
          }}
          data-testid="recipe-title-overlay"
        >
          {overlayBadgeText && (
            <Box
              position="absolute"
              start={50}
              positionTranslate="middle-x"
              style={{ top: '-3.6rem', zIndex: 12 }}
            >
              <Badge
                variant="primary"
                pill
                shadow="sm"
                style={{
                  backgroundColor: 'var(--bs-primary)',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.65)',
                  padding: '0.45rem 0.85rem',
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                }}
              >
                {overlayBadgeText}
              </Badge>
            </Box>
          )}

          <Box as="span" className="label-line-top" style={{ backgroundColor: labelColor }} aria-hidden="true">&nbsp;</Box>

          <Heading
            as="h5"
            textAlign="center"
            marginBottom={0}
            style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}
          >
            {recipe.title}
          </Heading>

          <Box as="span" className="label-line-bottom" style={{ backgroundColor: labelColor }} aria-hidden="true">&nbsp;</Box>

        </Box>

        {recipe.authors && (
          <Flex
            align="center"
            className="author-sticker"
            position="absolute"
            top={0}
            start={0}
            background="white"
            rounded
            margin={2}
            padding={2}
            style={{ boxShadow: `0px 2px 5px rgba(0, 0, 0, 0.2)` }}
          >
            {recipe.authors.avatar_url && (
              <Avatar
                src={recipe.authors.avatar_url}
                alt={`${recipe.authors.first_name} ${recipe.authors.name}`}
                marginEnd={1}
                size={32}
              />
            )}
            <Text as="span" fontWeight="bold" marginBottom={0} style={{ fontSize: '1em', fontStyle: 'normal' }}>
              {recipe.authors.first_name} {recipe.authors.name}
            </Text>
          </Flex>
        )}

        {recipe.tags && recipe.tags.length > 0 && (
          <Box position="absolute" end={0} padding={2} style={{ bottom: '100px' }}>
            {recipe.tags.map(tag => (
              <Badge key={tag.id} pill marginEnd={1} marginBottom={1}>
                {tag.name}
              </Badge>
            ))}
          </Box>
        )}

        <Box
          data-testid="recipe-card-favorite-button"
          position="absolute"
          top={0}
          end={0}
          margin={3}
          style={{ zIndex: 15 }}
        >
          <FavoriteButton
            isFavorite={isFavorite}
            onClick={onFavoriteClick}
            disabled={favoriteDisabled}
          />
        </Box>
      </BoxLink>
    </Card>
  );
};

const MemoizedRecipeCard = React.memo(RecipeCardComponent);
export default MemoizedRecipeCard;
